/**
 * Which clinical record the portal is acting on, for this tab.
 *
 * A module-level store rather than React state, for two reasons. The axios
 * interceptor has to read it synchronously and is not a component. And the
 * value has to survive route changes, which a provider mounted inside a route
 * element would not.
 *
 * Per tab, deliberately: someone enrolled in two courses keeps two panels open
 * at once, so this lives in `sessionStorage`. `localStorage` would share the
 * choice across tabs and re-create the sticky "current record" the design
 * rejects — one tab would silently start writing into the other's record.
 */

const STORAGE_KEY = "alchely.portal.activePatientId";

type Listener = () => void;

const listeners = new Set<Listener>();
let activePatientId: string | null = read();

function read(): string | null {
    try {
        return sessionStorage.getItem(STORAGE_KEY);
    } catch {
        // Private windows and blocked site data throw on access. The portal
        // still works without a remembered choice; it just asks again.
        return null;
    }
}

function write(patientId: string | null): void {
    try {
        if (patientId === null) sessionStorage.removeItem(STORAGE_KEY);
        else sessionStorage.setItem(STORAGE_KEY, patientId);
    } catch {
        // Nothing to do: the value is still held in memory for this tab.
    }
}

/** Read synchronously. Used by the axios interceptor. */
export function getActivePatientId(): string | null {
    return activePatientId;
}

export function setActivePatientId(patientId: string | null): void {
    if (patientId === activePatientId) return;
    activePatientId = patientId;
    write(patientId);
    listeners.forEach((listener) => listener());
}

export function subscribeToActivePatientId(listener: Listener): () => void {
    listeners.add(listener);
    return () => {
        listeners.delete(listener);
    };
}

/** Forget the choice — on sign-out, so the next patient is asked afresh. */
export function clearActivePatientId(): void {
    setActivePatientId(null);
}
